// PendingCreatePage — new-pending form. Mirrors legacy `pending_bookings.php`
// inline "New Pending Booking" form (Customer Information, Vehicle
// Information, Booking Details, Initial Notes).
//
// Submit: POST /pending. The optional Initial Notes textarea is sent as the
// `notes` field on the row (legacy parity — the row's `notes` column is the
// initial-notes display on the detail page).

import { useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Input } from '../../components/form/Input';
import { Textarea } from '../../components/form/Textarea';
import { Select } from '../../components/form/Select';
import { useToast } from '../../hooks/useToast';
import { useCreatePending } from './hooks/usePending';
import {
  useProducts,
  useWorkSources,
} from '../bookings/hooks/useReferenceData';
import type { CreatePendingPayload } from '../../types/api';
import styles from './PendingCreatePage.module.css';

interface FormState {
  name: string;
  email: string;
  phone: string;
  phone2: string;
  postcode: string;
  address: string;
  car_make_model: string;
  car_registration: string;
  price: string;
  product_id: string;
  source: string;
  notes: string;
}

const EMPTY: FormState = {
  name: '',
  email: '',
  phone: '',
  phone2: '',
  postcode: '',
  address: '',
  car_make_model: '',
  car_registration: '',
  price: '',
  product_id: '',
  source: '',
  notes: '',
};

export function PendingCreatePage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const create = useCreatePending();
  const products = useProducts();
  const sources = useWorkSources();

  const [state, setState] = useState<FormState>(EMPTY);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setState((prev) => ({ ...prev, [key]: value }));
  }

  const productOptions = useMemo(
    () => [
      { value: '', label: '-- Select --' },
      ...(products.data ?? []).map((p) => ({ value: String(p.id), label: p.name })),
    ],
    [products.data],
  );

  const sourceOptions = useMemo(
    () => [
      { value: '', label: '-- Select --' },
      ...(sources.data ?? []).map((s) => ({ value: String(s.id), label: s.name })),
    ],
    [sources.data],
  );

  const requiredFilled =
    state.name.trim().length > 0 &&
    state.phone.trim().length > 0 &&
    state.address.trim().length > 0 &&
    state.postcode.trim().length > 0 &&
    state.car_make_model.trim().length > 0 &&
    state.car_registration.trim().length > 0;

  const submitting = create.isPending;
  const canSubmit = requiredFilled && !submitting;

  async function handleSubmit() {
    if (!canSubmit) return;
    const payload: CreatePendingPayload = {
      name: state.name.trim(),
      phone: state.phone.trim(),
      address: state.address.trim(),
      postcode: state.postcode.trim(),
      car_make_model: state.car_make_model.trim(),
      car_registration: state.car_registration.trim(),
      ...(state.email.trim().length > 0 ? { email: state.email.trim() } : {}),
      ...(state.phone2.trim().length > 0 ? { phone2: state.phone2.trim() } : {}),
      ...(state.price.length > 0 ? { price: Number(state.price) } : {}),
      ...(state.product_id.length > 0
        ? { product_id: Number(state.product_id) }
        : {}),
      ...(state.source.length > 0 ? { source: Number(state.source) } : {}),
      ...(state.notes.trim().length > 0 ? { notes: state.notes.trim() } : {}),
    };

    try {
      const result = await create.mutateAsync(payload);
      toast.success(`Pending booking ${result.pending_booking.reference} created.`);
      navigate(`/collections/pending/${encodeURIComponent(result.pending_booking.enc_id)}`);
    } catch (err) {
      const msg = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message ??
          'Could not create pending booking.'
        : 'Could not create pending booking.';
      toast.error(msg);
    }
  }

  return (
    <div className={styles.page}>
      <button type="button" onClick={() => navigate('/collections/pending')} className={styles.back}>
        <ChevronLeft size={14} aria-hidden /> Back to pending
      </button>

      <PageHeader title="Add Pending Booking" />

      <Card>
        <h3 className={styles.sectionTitle}>Customer information</h3>
        <div className={styles.grid}>
          <Input
            label={<>Name <span aria-hidden>*</span></>}
            required
            value={state.name}
            onChange={(e) => set('name', e.target.value)}
          />
          <Input
            label="Email"
            variant="email"
            value={state.email}
            onChange={(e) => set('email', e.target.value)}
          />
          <Input
            label={<>Phone <span aria-hidden>*</span></>}
            required
            value={state.phone}
            onChange={(e) => set('phone', e.target.value)}
          />
          <Input
            label="Secondary phone"
            value={state.phone2}
            onChange={(e) => set('phone2', e.target.value)}
          />
          <Input
            label={<>Postcode <span aria-hidden>*</span></>}
            required
            value={state.postcode}
            onChange={(e) => set('postcode', e.target.value)}
          />
          <div className={styles.fullRow}>
            <Textarea
              label={<>Address <span aria-hidden>*</span></>}
              required
              value={state.address}
              onChange={(e) => set('address', e.target.value)}
              rows={2}
              autoResize
            />
          </div>
        </div>
      </Card>

      <Card>
        <h3 className={styles.sectionTitle}>Vehicle information</h3>
        <div className={styles.grid}>
          <Input
            label={<>Make &amp; model <span aria-hidden>*</span></>}
            required
            value={state.car_make_model}
            onChange={(e) => set('car_make_model', e.target.value)}
          />
          <Input
            label={<>Registration <span aria-hidden>*</span></>}
            required
            value={state.car_registration}
            onChange={(e) => set('car_registration', e.target.value)}
          />
          <Input
            label="Price (£)"
            variant="number"
            step="0.01"
            min="0"
            value={state.price}
            onChange={(e) => set('price', e.target.value)}
          />
        </div>
      </Card>

      <Card>
        <h3 className={styles.sectionTitle}>Booking details</h3>
        <div className={styles.grid}>
          <Select
            label="Product"
            options={productOptions}
            value={state.product_id}
            onChange={(e) => set('product_id', e.target.value)}
          />
          <Select
            label="Source"
            options={sourceOptions}
            value={state.source}
            onChange={(e) => set('source', e.target.value)}
          />
        </div>
      </Card>

      <Card>
        <h3 className={styles.sectionTitle}>Initial notes (optional)</h3>
        <Textarea
          label="Initial notes"
          value={state.notes}
          onChange={(e) => set('notes', e.target.value)}
          rows={3}
          autoResize
          placeholder="Any special requirements or notes about this pending booking"
        />
      </Card>

      <div className={styles.actions}>
        <Button variant="secondary" onClick={() => navigate('/collections/pending')} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => void handleSubmit()}
          loading={submitting}
          disabled={!canSubmit}
        >
          Save Pending
        </Button>
      </div>
    </div>
  );
}
